import {
  Box,
  Button,
  HStack,
  Icon,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Mail } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { useHotelsI18n } from '../../i18n'
import { useClearAllVoicemails, useListVoicemails } from '../../queries/hooks'
import type { EnrichedRoom, HospitalityPermissions } from '../../types/rooms'
import type { Voicemail } from '../../types/voicemail'
import { valueOrPlaceholder } from '../../utils/display'
import { formatVoicemailTime } from '../../utils/formatting'

const MAX_BADGE_COUNT = 9

interface VoicemailPopoverProps {
  room: EnrichedRoom
  api: HotelsApi
  pbxId: number
  toast: ToastLike
  permissions: HospitalityPermissions
}

interface VoicemailPopoverContentProps {
  room: EnrichedRoom
  api: HotelsApi
  pbxId: number
  toast: ToastLike
  permissions: HospitalityPermissions
}

const VoicemailPopoverContent = ({
  room,
  api,
  pbxId,
  toast,
  permissions,
}: VoicemailPopoverContentProps) => {
  const { text } = useHotelsI18n()
  const vmQuery = useListVoicemails({
    api,
    roomId: room.id,
    pbxId,
    limit: 4,
    ordering: '-created_epoch',
    options: { enabled: room.id > 0 && pbxId > 0 },
  })

  const voicemails: Voicemail[] = useMemo(
    () => vmQuery.data?.results ?? [],
    [vmQuery.data],
  )

  const clearAllMutation = useClearAllVoicemails({
    api,
    options: {
      onSuccess: (data) => {
        toast.create({
          title: text(
            'hotels.dashboard.voicemailPopover.clearAllSuccess',
            data.cleared_count === 1
              ? '{{count}} voicemail cleared'
              : '{{count}} voicemails cleared',
            {
              count: data.cleared_count,
            },
          ),
          type: 'success',
        })
      },
      onError: () =>
        toast.create({
          title: text(
            'hotels.dashboard.voicemailPopover.clearAllFailed',
            'Failed to clear voicemails',
          ),
          type: 'error',
        }),
    },
  })

  const handleDeleteAll = useCallback(() => {
    if (!permissions.canEditQueues) return
    clearAllMutation.mutate({ roomId: room.id, pbxId })
  }, [permissions.canEditQueues, clearAllMutation, room.id, pbxId])

  const canClearVoicemails = permissions.canEditQueues

  return (
    <Popover.Content
      w="380px"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Popover.Header p={3} borderBottomWidth={1}>
        <HStack justify="space-between" w="full">
          <HStack gap={2}>
            <Icon as={Mail} boxSize={4} color="fg.subtle" />
            <Text fontWeight="semibold" fontSize="sm">
              {text('hotels.dashboard.voicemailPopover.title', 'Voicemails')}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {room.number}
            </Text>
          </HStack>
          {voicemails.length > 0 && canClearVoicemails && (
            <Button
              size="xs"
              variant="subtle"
              borderRadius="l2"
              colorPalette="error"
              color="fg.error"
              _disabled={{ color: 'fg.error' }}
              onClick={handleDeleteAll}
              loading={clearAllMutation.isPending}
            >
              {text('hotels.dashboard.voicemailPopover.clearAll', 'Clear All?')}
            </Button>
          )}
        </HStack>
      </Popover.Header>

      <Popover.Body p={3} maxH="300px" overflowY="auto">
        {vmQuery.isLoading ? (
          <Text fontSize="xs" color="fg.muted" textAlign="center" py={2}>
            {text('hotels.dashboard.voicemailPopover.loading', 'Loading...')}
          </Text>
        ) : voicemails.length === 0 ? (
          <Text fontSize="xs" color="fg.muted" textAlign="center" py={2}>
            {text('hotels.dashboard.voicemailPopover.none', 'No voicemails')}
          </Text>
        ) : (
          <VStack align="stretch" gap={1}>
            <HStack fontSize="xs" color="fg.muted" px={1}>
              <Text w="50px">
                {text('hotels.dashboard.voicemailPopover.status', 'Status')}
              </Text>
              <Text flex={1}>
                {text('hotels.dashboard.voicemailPopover.received', 'Received')}
              </Text>
              <Text w="100px">
                {text('hotels.dashboard.voicemailPopover.from', 'From')}
              </Text>
            </HStack>
            {voicemails.map((vm: Voicemail) => (
              <HStack
                key={vm.id}
                fontSize="xs"
                px={1}
                py={1}
                borderRadius="sm"
                _hover={{ bg: 'bg.subtle' }}
              >
                <Text w="50px">
                  {(vm.is_unread ??
                  (typeof vm.read_epoch === 'number'
                    ? vm.read_epoch === 0
                    : !vm.read_at))
                    ? text('hotels.dashboard.voicemailPopover.new', 'New')
                    : text('hotels.dashboard.voicemailPopover.read', 'Read')}
                </Text>
                <Text flex={1}>
                  {formatVoicemailTime(vm.created_epoch ?? vm.created_at)}
                </Text>
                <Text w="100px" truncate>
                  {valueOrPlaceholder(
                    vm.from_phone_number?.phone_number ?? vm.caller_id_number,
                    text,
                  )}
                </Text>
              </HStack>
            ))}
          </VStack>
        )}
      </Popover.Body>
    </Popover.Content>
  )
}

export const VoicemailPopover = ({
  room,
  api,
  pbxId,
  toast,
  permissions,
}: VoicemailPopoverProps) => {
  const { text } = useHotelsI18n()
  const [isOpen, setIsOpen] = useState(false)
  const voicemailCount = room.voicemail_count ?? 0
  const hasVoicemails = voicemailCount > 0
  const triggerLabel = hasVoicemails
    ? text(
        'hotels.dashboard.voicemailPopover.manageAriaWithCount',
        voicemailCount === 1
          ? 'Manage voicemails ({{count}} voicemail)'
          : 'Manage voicemails ({{count}} voicemails)',
        { count: voicemailCount },
      )
    : text('hotels.dashboard.voicemailPopover.manageAria', 'Manage voicemails')

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      positioning={{ placement: 'right-start' }}
    >
      <Popover.Trigger asChild>
        <Box
          as="button"
          position="relative"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          aria-label={triggerLabel}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          p={1.5}
          borderRadius="lg"
          bg={hasVoicemails ? 'orange.100' : 'bg.muted'}
          _hover={{ bg: hasVoicemails ? 'orange.200' : 'bg.subtle' }}
        >
          <Icon
            as={Mail}
            boxSize={5}
            color={hasVoicemails ? 'orange.700' : 'fg.subtle'}
          />
          {hasVoicemails && (
            <Box
              as="span"
              aria-hidden="true"
              data-testid="voicemail-count-badge"
              position="absolute"
              top="-1"
              right="-1"
              minW="4"
              h="4"
              px="1"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="bg.inverted"
              color="fg.inverted"
              fontSize="2xs"
              fontWeight="semibold"
              lineHeight="1"
            >
              {voicemailCount > MAX_BADGE_COUNT
                ? `${MAX_BADGE_COUNT}+`
                : voicemailCount}
            </Box>
          )}
        </Box>
      </Popover.Trigger>
      {isOpen && (
        <Portal>
          <Popover.Positioner>
            <VoicemailPopoverContent
              room={room}
              api={api}
              pbxId={pbxId}
              toast={toast}
              permissions={permissions}
            />
          </Popover.Positioner>
        </Portal>
      )}
    </Popover.Root>
  )
}
